import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowRight, Award, Users, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { IMAGES, CONTACT } from '../constants/images';
import { SEO } from '../components/SEO';
import { ScrollReveal } from '../components/ScrollReveal';

export const Home = () => {
  const conditions = [
    'Varicose Veins - laser ablation, glue therapy & sclerotherapy',
    'Peripheral Artery Disease - angioplasty & stenting',
    'Diabetic Foot Ulcers - limb salvage without amputation',
    'Deep Vein Thrombosis & Pulmonary Embolism',
    'Liver Tumours - TACE, TARE & microwave ablation',
    'Uterine Fibroids - UAE (uterus-preserving treatment)',
    'Dialysis Access Failure - AV fistula angioplasty',
    'Emergency Hemorrhage - trauma & GI bleeding treatment'
  ];

  const services = [
    {
      title: 'Leg & Foot Care',
      icon: '🦵',
      description: 'Varicose veins, PAD, DVT, diabetic foot - laser ablation, angioplasty & stenting',
      path: '/services/leg-foot-care'
    },
    {
      title: 'Liver & GI Interventions',
      icon: '🫀',
      description: 'TACE, TARE, TIPS, biliary stenting, liver abscess drainage',
      path: '/services/liver-gi'
    },
    {
      title: 'Kidney & Dialysis Access',
      icon: '🫘',
      description: 'AV fistula angioplasty, fistuloplasty, declotting, PCN',
      path: '/services/kidney-dialysis'
    },
    {
      title: 'Women\'s Health',
      icon: '♀️',
      description: 'Uterine Artery Embolization for fibroids & ovarian vein embolization',
      path: '/services/womens-health'
    },
    {
      title: 'Emergency Interventions',
      icon: '🚨',
      description: 'Trauma bleeding, GI haemorrhage, postpartum haemorrhage',
      path: '/services/emergency'
    },
    {
      title: 'Miscellaneous Procedures',
      icon: '➕',
      description: 'PICC lines, permcath, varicocele embolization',
      path: '/services/miscellaneous'
    }
  ];

  const whyChoose = [
    {
      icon: <Award className="w-8 h-8 text-amber-600" />,
      title: 'FVIR with Distinction',
      description: 'Gold standard IR credential from KEM Hospital, Mumbai'
    },
    {
      icon: <Users className="w-8 h-8 text-amber-600" />,
      title: 'KEM, Tata Memorial & Bombay Hospital',
      description: 'Three of Mumbai\'s finest institutions'
    },
    {
      icon: <Clock className="w-8 h-8 text-amber-600" />,
      title: 'Day-Care Procedures',
      description: 'Most procedures use local anaesthesia only'
    },
    {
      icon: <Shield className="w-8 h-8 text-amber-600" />,
      title: 'Honest, Patient-First',
      description: 'Never over-treats. Consistently praised for transparency'
    }
  ];

  return (
    <>
      <SEO 
        title="Interventional Radiologist in Mumbai | Dr. Punit Mahajan"
        description="Dr. Punit Mahajan is a fellowship-trained Interventional Radiologist in Mumbai offering minimally invasive treatments for Peripheral arterial diseases, diabetic foot, varicose veins, liver cancers, uterine fibroids, dialysis access salvage & more. Book now."
      />

      {/* Hero Section */}
      <section className="relative hero-bg-animated text-white overflow-hidden">
        {/* Pattern overlay */}
        <div className="absolute inset-0 pattern-hero opacity-30"></div>
        
        {/* Animated glow orbs */}
        <div className="hero-glow" style={{ top: '10%', left: '5%' }}></div>
        <div className="hero-glow" style={{ bottom: '10%', right: '5%', animationDelay: '2s' }}></div>
        
        {/* Decorative rotating ring */}
        <div className="absolute top-20 right-20 w-96 h-96 opacity-10 hidden lg:block">
          <div className="w-full h-full border-2 border-amber-400 rounded-full animate-rotate-slow"></div>
          <div className="absolute inset-8 border border-amber-300 rounded-full animate-rotate-slow" style={{ animationDirection: 'reverse', animationDuration: '20s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block animate-text-reveal" style={{ animationDelay: '100ms' }}>
                <span className="bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  Fellowship-Trained FVIR • KEM Hospital Mumbai
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight animate-text-reveal" style={{ animationDelay: '300ms' }}>
                Interventional Radiologist in Mumbai — 
                <span className="text-amber-500 inline-block animate-text-reveal" style={{ animationDelay: '600ms' }}> Minimally Invasive. Precision-Led. Life-Changing.</span>
              </h1>
              
              <p className="text-xl text-slate-300 leading-relaxed animate-text-reveal" style={{ animationDelay: '900ms' }}>
                Expert image-guided treatments for vascular disease, liver tumours, uterine fibroids, dialysis access & bleeding emergencies — without open surgery, without general anaesthesia.
              </p>
              
              <div className="bg-blue-800/40 backdrop-blur-sm border border-amber-600/30 rounded-xl p-6 hover-glow relative overflow-hidden animate-text-reveal" style={{ animationDelay: '1100ms' }}>
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-400 via-amber-500 to-amber-600"></div>
                <p className="text-lg italic text-slate-200 mb-2">
                  "Minimally invasive care is not just about pin hole procedures — it is about precision, faster recovery, and restoring lives with the least possible disruption."
                </p>
                <p className="text-sm text-amber-500 font-semibold">— Dr. Punit Mahajan, MD, DNB, FVIR</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-text-reveal" style={{ animationDelay: '1300ms' }}>
                <a href={`tel:${CONTACT.phone}`}>
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 hover-lift">
                    <Phone className="w-5 h-5 mr-2" />
                    Book Appointment
                  </Button>
                </a>
                <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 hover-lift bg-transparent">
                    WhatsApp Now
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="relative animate-scale-in animate-delay-300">
              <div className="hero-image-wrapper">
                {/* Decorative corner accents - bright amber */}
                <div className="absolute -top-3 -left-3 w-16 h-16 border-t-4 border-l-4 border-amber-500 rounded-tl-3xl z-20"></div>
                <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-4 border-r-4 border-amber-500 rounded-br-3xl z-20"></div>
                
                <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={IMAGES.doctor.hero} 
                    alt="Dr. Punit Mahajan - Interventional Radiologist Mumbai"
                    className="w-full h-auto"
                  />
                </div>
              </div>
              
              {/* Static badges */}
              <div className="absolute -top-4 -right-4 bg-white text-blue-900 px-4 py-3 rounded-xl shadow-2xl hidden md:block animate-scale-in animate-delay-700 border-2 border-amber-500">
                <p className="text-2xl font-bold text-amber-600">15+</p>
                <p className="text-xs font-medium">Publications</p>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-amber-600 text-white px-4 py-3 rounded-xl shadow-2xl hidden md:block animate-scale-in" style={{ animationDelay: '900ms' }}>
                <p className="text-2xl font-bold">FVIR</p>
                <p className="text-xs font-medium">with Distinction</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave divider at bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-12 md:h-16" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,60 C150,100 350,0 600,40 C850,80 1050,20 1200,60 L1200,120 L0,120 Z" fill="#fdfbf6"/>
          </svg>
        </div>
      </section>

      {/* Common Conditions */}
      <section className="py-16 bg-cream-pattern relative overflow-hidden">
        {/* Decorative gradient blobs */}
        <div className="absolute -top-32 left-1/4 w-96 h-96 rounded-full opacity-20 hidden md:block" style={{ background: 'radial-gradient(circle, rgba(252, 211, 77, 0.4), transparent 60%)' }}></div>
        <div className="absolute -bottom-20 right-0 w-80 h-80 rounded-full opacity-20 hidden md:block" style={{ background: 'radial-gradient(circle, rgba(254, 215, 170, 0.5), transparent 60%)' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <div className="gold-ornament mb-4">Expertise</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Common Conditions Treated by Dr. Punit Mahajan in Mumbai
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Many patients benefit from Interventional Radiology when traditional surgery poses risks or when targeted, image-guided treatment delivers better outcomes.
            </p>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {conditions.map((condition, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 80}>
                <div className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm border border-amber-100 hover:border-amber-400 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
                  <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{condition}</span>
                </div>
              </ScrollReveal>
            ))}
          </div>
          
          <ScrollReveal animation="fade-up" delay={300} className="text-center mt-8">
            <Link to="/services">
              <Button size="lg" className="bg-blue-900 hover:bg-blue-800 text-white">
                View All Services <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-cream-textured relative overflow-hidden">
        {/* Decorative SVG element */}
        <div className="absolute top-10 right-10 w-32 h-32 opacity-20 hidden md:block">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#d97706" strokeWidth="0.5" strokeDasharray="2 4" />
            <circle cx="50" cy="50" r="30" fill="none" stroke="#d97706" strokeWidth="0.5" />
          </svg>
        </div>
        <div className="absolute bottom-10 left-10 w-24 h-24 opacity-20 hidden md:block">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <path d="M50 5 L95 50 L50 95 L5 50 Z" fill="none" stroke="#d97706" strokeWidth="0.5" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <div className="gold-ornament mb-4">Our Services</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Six Specialized Categories of Care
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Full spectrum of minimally invasive Interventional Radiology services
            </p>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <Card className="card-gold-hover bg-white group h-full">
                  <CardContent className="p-6">
                    <div className="text-4xl mb-4 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">{service.icon}</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-700 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    <Link to={service.path} className="text-blue-900 hover:text-amber-600 font-semibold inline-flex items-center group/link">
                      Learn More 
                      <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover/link:translate-x-1" />
                    </Link>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>
        {/* Soft decorative orb */}
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-30 hidden md:block" style={{ background: 'radial-gradient(circle, rgba(254, 215, 170, 0.5), transparent 70%)' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal animation="fade-right">
              <div className="relative">
                <img 
                  src={IMAGES.doctor.desk2} 
                  alt="Dr. Punit Mahajan - About"
                  className="rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -right-6 bg-amber-600 text-white p-6 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">15+</p>
                  <p className="text-sm">Publications</p>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal animation="fade-left" delay={200}>
              <div className="space-y-6">
                <div className="gold-ornament">About</div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Dr. Punit Mahajan — Interventional Radiologist in Mumbai
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Dr. Punit Pushkar Mahajan, MD, DNB, FVIR, CCNR, MNAMS, is a fellowship-trained Interventional Radiologist practicing across Mumbai's most trusted hospitals. He completed his FVIR (with Distinction) at Seth GS Medical College & KEM Hospital, his MD Radiology at Bombay Hospital Institute of Medical Sciences, and his Senior Residency at Tata Memorial Hospital.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  A former Assistant Professor at KEM Hospital with 15+ publications, Dr. Punit is a member of ISVIR, CIRSE, and MNAMS. His patients describe him as honest, patient, and genuinely invested in their wellbeing.
                </p>
                <Link to="/about">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                    Read Full Profile <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 bg-cream-textured relative overflow-hidden">
        {/* Floating decorative elements */}
        <div className="absolute top-20 left-10 w-3 h-3 rounded-full opacity-40 hidden md:block" style={{ background: '#d97706' }}></div>
        <div className="absolute top-40 right-20 w-2 h-2 rounded-full opacity-50 hidden md:block" style={{ background: '#d4b483' }}></div>
        <div className="absolute bottom-20 left-1/4 w-4 h-4 rounded-full opacity-30 hidden md:block" style={{ background: '#fcd34d' }}></div>
        <div className="absolute bottom-40 right-1/3 w-2 h-2 rounded-full opacity-40 hidden md:block" style={{ background: '#d97706' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal animation="fade-up" className="text-center mb-12">
            <div className="gold-ornament mb-4">Why Choose Us</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Dr. Punit Mahajan?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Why patients and referring clinicians trust Dr. Punit Mahajan for Interventional Radiology in Mumbai
            </p>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((item, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 120}>
                <div className="text-center space-y-4 bg-white/60 backdrop-blur-sm p-6 rounded-xl border border-amber-100 hover:border-amber-300 hover:shadow-md hover-lift transition-all h-full">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-50 rounded-full border-2 border-amber-200 transition-transform duration-500 hover:scale-110 hover:rotate-6">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20"></div>
        <ScrollReveal animation="scale" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to Explore a Minimally Invasive Solution?
          </h2>
          <p className="text-xl text-slate-300">
            Book a consultation with Dr. Punit Mahajan — Interventional Radiologist in Mumbai
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${CONTACT.phone}`}>
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 hover-lift">
                <Phone className="w-5 h-5 mr-2" />
                {CONTACT.phone}
              </Button>
            </a>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 hover-lift bg-transparent">
                WhatsApp
              </Button>
            </a>
            <a href={`mailto:${CONTACT.email}`}>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 hover-lift bg-transparent">
                Email Us
              </Button>
            </a>
          </div>
        </ScrollReveal>
      </section>
    </>
  );
};
